import type {
	DashEnergyWidgetConfig,
	DashEnergyWidgetTypes,
} from 'src/components/Dashboards/dash-energy-types';
import type { DashboardRecord } from 'src/components/Dashboards/dash-types';

export const ENERGY_V2_SCHEMA_VERSION = 6;
export const ENERGY_DASHBOARD_ID = -103;
export const ENERGY_DASHBOARD_ROUTE = '/energy_monitoring';
/** Virtual, client-only dashboard id. Negative ids cannot collide with persisted dashboards. */
export const ALL_ENERGY_WIDGETS_DASHBOARD_ID = -104;
export const ALL_ENERGY_WIDGETS_DASHBOARD_ROUTE = '/-104';

export type EnergyPurposeId = 'pv' | 'home' | 'devices';

export const ENERGY_V2_PRESETS: Array<{
	id: EnergyPurposeId;
	icon: string;
	labelKey: string;
	labelDefault: string;
	descriptionKey: string;
	descriptionDefault: string;
	bestUsageKey: string;
	bestUsageDefault: string;
}> = [
	{
		id: 'pv',
		icon: 'fas fa-solar-panel',
		labelKey: 'energy_v3_purpose_pv',
		labelDefault: 'PV setup',
		descriptionKey: 'energy_v3_purpose_pv_description',
		descriptionDefault: 'Solar production, self-consumption, and energy flow.',
		bestUsageKey: 'dashboard_energy_preset_pv_best_usage',
		bestUsageDefault: 'Best for homes with configured grid and solar meters.',
	},
	{
		id: 'home',
		icon: 'fas fa-house',
		labelKey: 'energy_v3_purpose_home',
		labelDefault: 'Home energy',
		descriptionKey: 'energy_v3_purpose_home_description',
		descriptionDefault: 'Whole-home consumption, costs, and usage patterns.',
		bestUsageKey: 'dashboard_energy_preset_home_best_usage',
		bestUsageDefault: 'Best for understanding total demand, costs, and peak periods.',
	},
	{
		id: 'devices',
		icon: 'fat fa-objects-column',
		labelKey: 'energy_v3_purpose_devices',
		labelDefault: 'Devices Energy',
		descriptionKey: 'energy_v3_purpose_devices_description',
		descriptionDefault: 'Device comparisons, peaks, and room consumption.',
		bestUsageKey: 'dashboard_energy_preset_devices_best_usage',
		bestUsageDefault: 'Best for comparing metered devices and rooms.',
	},
];

export const PROTECTED_ENERGY_V2_CAPABILITIES = {
	allowAddWidgets: true,
	allowManageWidgets: true,
	allowReorderWidgets: true,
} as const;

export type EnergyV2Widget = {
	id: string;
	type: DashEnergyWidgetTypes;
	col: 'left' | 'right';
	position: number;
	title: string;
	showWidgetTitle?: boolean;
	energyConfig: DashEnergyWidgetConfig;
	cards: Array<{
		id: string;
		_type: 'energy';
		position: number;
	}>;
};

export type EnergyV2PurposeLayout = {
	widgets: EnergyV2Widget[];
};

export type EnergyV2Config = {
	schemaVersion: number;
	activePurpose: EnergyPurposeId;
	purposes: Record<EnergyPurposeId, EnergyV2PurposeLayout>;
};

type WidgetDefinition = Omit<EnergyV2Widget, 'cards' | 'position'> & {
	position?: number;
};

const widget = (definition: WidgetDefinition): EnergyV2Widget => ({
	...definition,
	showWidgetTitle: definition.showWidgetTitle ?? true,
	position: definition.position ?? 0,
	cards: [
		{
			id: definition.id,
			_type: 'energy',
			position: 0,
		},
	],
});

const infoWidget = (
	id: string,
	col: EnergyV2Widget['col'],
	infoCategories: NonNullable<DashEnergyWidgetConfig['infoCategories']>,
): EnergyV2Widget => ({
	...widget({
		id,
		type: 'dashEnergyInfo',
		col,
		title: 'Energy overview',
		energyConfig: {
			period: 'live',
			infoCategories,
			showTariffBar: true,
		},
	}),
	cards: infoCategories.map((category, position) => ({
		id: category,
		_type: 'energy',
		position,
	})),
});

const periodSummaryWidget = (purpose: EnergyPurposeId): EnergyV2Widget =>
	widget({
		id: `${purpose}-period-summary`,
		type: 'dashEnergyKpiPeriod',
		col: 'right',
		title: 'Usage and cost',
		energyConfig: { period: '24_hours', showTariffBar: true },
	});

const positionColumns = (widgets: EnergyV2Widget[]): EnergyV2Widget[] => {
	const positions = { left: 0, right: 0 };
	return widgets.map((entry) => ({
		...entry,
		position: positions[entry.col]++,
	}));
};

/**
 * Default layouts for the dedicated Energy dashboard (`/dash/energy_monitoring`).
 *
 * Mobile stacks left then right, so column 1 is the first-glance set:
 * widgets that work from any power-measuring devices. Column 2 is PV, tariff,
 * rooms, sensors, and other setup-dependent views.
 */
export const ENERGY_V2_PURPOSE_TEMPLATES: Record<EnergyPurposeId, EnergyV2PurposeLayout> = {
	pv: {
		widgets: positionColumns([
			infoWidget('pv-overall-info', 'left', ['solar', 'load', 'selfPowered', 'gridImport']),
			periodSummaryWidget('pv'),
			widget({
				id: 'pv-consumer-mix',
				type: 'dashEnergyConsumerMix',
				col: 'left',
				title: 'Consumer mix',
				energyConfig: { period: 'live', minSharePercent: 3, showLegend: true },
			}),
			widget({
				id: 'pv-distribution',
				type: 'dashEnergyPvDistribution',
				col: 'right',
				title: 'Live energy distribution',
				energyConfig: { period: 'live' },
			}),
			widget({
				id: 'pv-generation',
				type: 'dashEnergyGeneration',
				col: 'right',
				title: 'Generation vs consumption',
				energyConfig: { period: '24_hours' },
			}),
			widget({
				id: 'pv-flow',
				type: 'dashEnergyFlow',
				col: 'right',
				title: 'Energy flow',
				energyConfig: {
					period: 'live',
					showSupply: true,
					supplyMode: 'auto',
					showRooms: true,
					showDevices: true,
					maxRoomNodes: 6,
					maxDeviceNodes: 8,
				},
			}),
		]),
	},
	home: {
		widgets: positionColumns([
			infoWidget('home-overall-info', 'left', ['load', 'solar', 'selfPowered', 'gridImport']),
			periodSummaryWidget('home'),
			widget({
				id: 'home-top-consumers',
				type: 'dashEnergyTopConsumers',
				col: 'left',
				title: 'Top consumers',
				energyConfig: { period: 'live', limit: 5, groupBy: 'device' },
			}),
			widget({
				id: 'home-consumer-mix',
				type: 'dashEnergyConsumerMix',
				col: 'left',
				title: 'Consumer mix',
				energyConfig: { period: 'live', minSharePercent: 3, showLegend: true },
			}),
			widget({
				id: 'home-heatmap',
				type: 'dashEnergyHeatmap',
				col: 'left',
				title: 'Yearly Heatmap',
				energyConfig: {
					period: 'year',
					heatmapScope: 'all',
					heatmapMetric: 'consumption',
					heatmapVariant: 'full',
					allowInWidgetMetricToggle: true,
					showPeriodTotal: true,
				},
			}),
			widget({
				id: 'home-peak',
				type: 'dashEnergyPeak',
				col: 'left',
				title: 'Peak Power Overview',
				energyConfig: { period: 'live', peakVariant: 'overview' },
			}),
			widget({
				id: 'home-generation',
				type: 'dashEnergyGeneration',
				col: 'right',
				title: 'Generation vs consumption',
				energyConfig: { period: '24_hours' },
			}),
			widget({
				id: 'home-flow',
				type: 'dashEnergyFlow',
				col: 'right',
				title: 'Energy flow',
				energyConfig: {
					period: 'live',
					showSupply: true,
					supplyMode: 'auto',
					showRooms: true,
					showDevices: true,
					maxRoomNodes: 6,
					maxDeviceNodes: 8,
				},
			}),
			widget({
				id: 'home-tariff',
				type: 'dashEnergyTariff',
				col: 'right',
				title: 'Tariff pressure',
				energyConfig: { period: '24_hours' },
			}),
		]),
	},
	devices: {
		widgets: positionColumns([
			infoWidget('devices-overall-info', 'left', ['load', 'topConsumer', 'standby', 'gridImport']),
			periodSummaryWidget('devices'),
			widget({
				id: 'devices-consumer-mix',
				type: 'dashEnergyConsumerMix',
				col: 'left',
				title: 'Consumer mix',
				energyConfig: { period: 'live', minSharePercent: 6, showLegend: true },
			}),
			widget({
				id: 'devices-heatmap',
				type: 'dashEnergyHeatmap',
				col: 'left',
				title: 'Monthly Heatmap',
				energyConfig: {
					period: 'live',
					heatmapScope: 'all',
					heatmapMetric: 'consumption',
					heatmapVariant: 'minimal',
					allowInWidgetMetricToggle: false,
					showPeriodTotal: true,
				},
			}),
			widget({
				id: 'devices-explore',
				type: 'dashEnergyInsights',
				col: 'left',
				title: 'Explore Energy Widgets',
				energyConfig: { period: 'live' },
			}),
			widget({
				id: 'devices-room-distribution',
				type: 'dashEnergyRoomDistribution',
				col: 'right',
				title: 'Energy Distribution By Room - 24h',
				energyConfig: { period: '24_hours' },
			}),
		]),
	},
};

export const cloneEnergyV2Widgets = (widgets: EnergyV2Widget[]): EnergyV2Widget[] =>
	widgets.map((entry) => ({
		...entry,
		energyConfig: { ...entry.energyConfig },
		cards: entry.cards.map((card) => ({ ...card })),
	}));

const normalizeEnergyV2Widgets = (widgets: EnergyV2Widget[]): EnergyV2Widget[] =>
	cloneEnergyV2Widgets(widgets).map((entry) => {
		const normalizedEntry: EnergyV2Widget = {
			...entry,
			showWidgetTitle: true,
		};

		if (entry.type !== 'dashEnergyHeatmap') return normalizedEntry;

		const config = entry.energyConfig;
		const { roomId: _roomId, deviceId: _deviceId, ...baseConfig } = config;
		const requestedScope = config.heatmapScope;
		const heatmapScope =
			requestedScope === 'room' && config.roomId != null
				? 'room'
				: requestedScope === 'device' && Boolean(config.deviceId)
					? 'device'
					: 'all';

		const isLegacyHeatmapTitle =
			/^(energy heatmap|heatmap minimal|device energy heatmap|room energy heatmap)$/i.test(
				entry.title.trim(),
			);
		return {
			...normalizedEntry,
			title: isLegacyHeatmapTitle
				? config.heatmapVariant === 'minimal'
					? 'Monthly Heatmap'
					: 'Yearly Heatmap'
				: entry.title,
			energyConfig: {
				...baseConfig,
				heatmapScope,
				...(heatmapScope === 'room' ? { roomId: config.roomId } : {}),
				...(heatmapScope === 'device' ? { deviceId: config.deviceId } : {}),
			},
		};
	});

const clonePurpose = (purpose: EnergyV2PurposeLayout): EnergyV2PurposeLayout => ({
	widgets: cloneEnergyV2Widgets(purpose.widgets),
});

const V4_LIVE_OVERVIEWS: Record<EnergyPurposeId, DashEnergyWidgetConfig['infoCategories']> = {
	pv: ['solar', 'load', 'selfPowered', 'gridImport'],
	home: ['load', 'solar', 'selfPowered', 'gridImport'],
	devices: ['load', 'topConsumer', 'standby', 'gridImport'],
};

const migrateV4PurposeWidgets = (
	purpose: EnergyPurposeId,
	widgets: EnergyV2Widget[],
): EnergyV2Widget[] => {
	const overviewId = `${purpose}-overall-info`;
	const migrated = normalizeEnergyV2Widgets(widgets).map((entry) => {
		if (entry.id !== overviewId || entry.type !== 'dashEnergyInfo') return entry;
		const infoCategories = V4_LIVE_OVERVIEWS[purpose] ?? [];
		return {
			...entry,
			energyConfig: { ...entry.energyConfig, period: 'live' as const, infoCategories },
			cards: infoCategories.map((category, position) => ({
				id: category,
				_type: 'energy' as const,
				position,
			})),
		};
	});

	const summaryId = `${purpose}-period-summary`;
	if (migrated.some((entry) => entry.id === summaryId)) return migrated;
	const summary = cloneEnergyV2Widgets([periodSummaryWidget(purpose)])[0];
	return [
		...migrated.map((entry) =>
			entry.col === 'right' ? { ...entry, position: entry.position + 1 } : entry,
		),
		{ ...summary, position: 0 },
	];
};

export const createEnergyV2Config = (activePurpose: EnergyPurposeId = 'home'): EnergyV2Config => ({
	schemaVersion: ENERGY_V2_SCHEMA_VERSION,
	activePurpose,
	purposes: {
		pv: clonePurpose(ENERGY_V2_PURPOSE_TEMPLATES.pv),
		home: clonePurpose(ENERGY_V2_PURPOSE_TEMPLATES.home),
		devices: clonePurpose(ENERGY_V2_PURPOSE_TEMPLATES.devices),
	},
});

export const isEnergyPurposeId = (value: unknown): value is EnergyPurposeId =>
	value === 'pv' || value === 'home' || value === 'devices';

const isWidget = (value: unknown): value is EnergyV2Widget => {
	if (!value || typeof value !== 'object') return false;
	const candidate = value as Partial<EnergyV2Widget>;
	return (
		typeof candidate.id === 'string' &&
		typeof candidate.type === 'string' &&
		(candidate.col === 'left' || candidate.col === 'right') &&
		Array.isArray(candidate.cards)
	);
};

export const normalizeEnergyV2Config = (raw: unknown, activeWidgets?: unknown[]): EnergyV2Config => {
	const rawValue = raw && typeof raw === 'object' ? (raw as Partial<EnergyV2Config>) : null;
	if ([4, 5].includes(Number(rawValue?.schemaVersion))) {
		const activePurpose = isEnergyPurposeId(rawValue?.activePurpose)
			? rawValue.activePurpose
			: 'home';
		const migrated = createEnergyV2Config(activePurpose);
		(['pv', 'home', 'devices'] as const).forEach((purpose) => {
			const widgets = rawValue?.purposes?.[purpose]?.widgets;
			if (Array.isArray(widgets) && widgets.length > 0 && widgets.every(isWidget)) {
				migrated.purposes[purpose] = {
					widgets: migrateV4PurposeWidgets(purpose, widgets),
				};
			}
		});
		if (activeWidgets?.length && activeWidgets.every(isWidget)) {
			migrated.purposes[activePurpose] = {
				widgets: migrateV4PurposeWidgets(activePurpose, activeWidgets),
			};
		}
		return migrated;
	}

	if (!rawValue || Number(rawValue.schemaVersion) !== ENERGY_V2_SCHEMA_VERSION) {
		return createEnergyV2Config();
	}

	const value = rawValue;
	const normalized = createEnergyV2Config(
		isEnergyPurposeId(value.activePurpose) ? value.activePurpose : 'home',
	);

	(['pv', 'home', 'devices'] as const).forEach((purpose) => {
		const widgets = value.purposes?.[purpose]?.widgets;
		if (Array.isArray(widgets) && widgets.length > 0 && widgets.every(isWidget)) {
			normalized.purposes[purpose] = {
				widgets: normalizeEnergyV2Widgets(widgets),
			};
		}
	});

	if (activeWidgets?.length && activeWidgets.every(isWidget)) {
		normalized.purposes[normalized.activePurpose] = {
			widgets: normalizeEnergyV2Widgets(activeWidgets),
		};
	}

	return normalized;
};

export const restoreActiveEnergyV2Layout = (config: EnergyV2Config): EnergyV2Config => ({
	...config,
	purposes: {
		...config.purposes,
		[config.activePurpose]: clonePurpose(ENERGY_V2_PURPOSE_TEMPLATES[config.activePurpose]),
	},
});

export const cloneEnergyV2WidgetsForDashboard = (
	widgets: EnergyV2Widget[],
	dashboardId: string | number,
): EnergyV2Widget[] =>
	cloneEnergyV2Widgets(widgets).map((entry, widgetIndex) => {
		const id = `${entry.type}-${dashboardId}-${widgetIndex + 1}`;
		return {
			...entry,
			id,
			cards: entry.cards.map((card, cardIndex) => ({
				...card,
				id: `${card.id}-${dashboardId}-${widgetIndex + 1}-${cardIndex + 1}`,
			})),
		};
	});

export const buildEnergyV2CloneDashboard = (
	created: DashboardRecord,
	sourceWidgets: EnergyV2Widget[],
): DashboardRecord => {
	const cloned: DashboardRecord = {
		...created,
		kind: 'standard',
		protected: false,
		schemaVersion: 1,
		dashWidgets: cloneEnergyV2WidgetsForDashboard(sourceWidgets, created.id),
	};
	delete cloned.energyV2;
	delete cloned.energyV3;
	return cloned;
};

export const buildLegacyCustomEnergyV2Migration = (
	current: DashboardRecord,
	purpose: EnergyPurposeId,
): DashboardRecord => {
	const defaults = createEnergyV2Config(purpose).purposes[purpose].widgets;
	const migrated: DashboardRecord = {
		...current,
		kind: 'standard',
		protected: false,
		schemaVersion: 1,
		dashWidgets: cloneEnergyV2WidgetsForDashboard(defaults, current.id),
	};
	delete migrated.energyV2;
	delete migrated.energyV3;
	return migrated;
};
